import type React from "react";
import { useEffect, useRef } from "react";
import Button from "../../../../../../core/button/Button";
import Icon from "../../../../../../core/icon/Icon";
import ListItem from "../../../../../../core/list/ListItem";

interface AttributeDisplayProps {
  attributes: { [key: string]: any } | null;
  visible: boolean;
  onClose: () => void;
  onAttributeClick: (property: string, value: any) => void;
  cursorPosition?: { x: number; y: number } | null;
}

const AttributeDisplay: React.FC<AttributeDisplayProps> = ({
  attributes,
  visible,
  onClose,
  onAttributeClick,
  cursorPosition,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        visible &&
        panelRef.current &&
        !panelRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Handle Cesium empty click events (when clicking on map without hitting an object)
    const handleCesiumEmptyClick = () => {
      if (visible) {
        onClose();
      }
    };

    // Handle clicks on Cesium canvas directly - only for actual map clicks, not UI interactions
    const handleCesiumCanvasClick = (event: MouseEvent) => {
      if (visible) {
        // Check if the click target is a Cesium canvas or container
        const target = event.target as HTMLElement;

        // Only close if we're actually clicking on the map canvas, not on UI elements
        if (
          (target.id && target.id.includes("cesiumContainer")) ||
          target.tagName === "CANVAS"
        ) {
          onClose();
          return;
        }

        // Don't close for other elements - let the regular click outside handler deal with them
      }
    };

    if (visible) {
      // Add DOM event listener for regular elements
      document.addEventListener("mousedown", handleClickOutside);

      // Add custom event listener for Cesium map clicks
      window.addEventListener(
        "cesiumEmptyClick",
        handleCesiumEmptyClick as EventListener,
      );

      // Add direct listener for Cesium canvas clicks
      document.addEventListener("mousedown", handleCesiumCanvasClick);
    }

    return () => {
      // Clean up event listeners
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener(
        "cesiumEmptyClick",
        handleCesiumEmptyClick as EventListener,
      );
      document.removeEventListener("mousedown", handleCesiumCanvasClick);
    };
  }, [visible, onClose]);

  if (!visible || !attributes) {
    return null;
  }

  // Filter out null and undefined values before rendering
  const validAttributes = Object.entries(attributes).filter(
    ([_, value]) => value !== null && value !== undefined,
  );

  // Calculate position based on cursor position
  const getPositionStyle = () => {
    if (cursorPosition) {
      // Panel dimensions
      const panelWidth = 288; // w-72 = 288px
      const panelHeight = 430; // increased height estimate for safety
      const offset = 20; // Distance from cursor

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate optimal position
      let left: number;
      let top: number;

      // Horizontal positioning: prefer right side, fallback to left if needed
      if (cursorPosition.x + offset + panelWidth <= viewportWidth) {
        // Enough space on the right
        left = cursorPosition.x + offset;
      } else if (cursorPosition.x - offset - panelWidth >= 0) {
        // Enough space on the left
        left = cursorPosition.x - offset - panelWidth;
      } else {
        // Center horizontally if neither side has enough space
        left = Math.max(20, (viewportWidth - panelWidth) / 2);
      }

      // Vertical positioning: align with cursor height, just adjust if it would overflow
      if (cursorPosition.y + panelHeight > viewportHeight) {
        // Panel would go below screen, adjust to fit with 10px margin for safety
        top = viewportHeight - panelHeight - 10;
      } else {
        // Panel fits at cursor height, position normally
        top = cursorPosition.y;
      }

      // Ensure panel doesn't go off-screen with minimum margins
      left = Math.max(20, Math.min(left, viewportWidth - panelWidth - 20));
      top = Math.max(20, Math.min(top, viewportHeight - panelHeight - 20));

      const finalPosition = {
        left: `${left}px`,
        top: `${top}px`,
      };

      return finalPosition;
    }

    // Fallback to default position (top-left area)
    return {
      top: "80px",
      left: "384px",
    };
  };

  return (
    <div
      ref={panelRef}
      className="fixed z-50 w-72 bg-surface-default border border-border-default rounded-2xl shadow-lg max-h-[80vh] overflow-hidden"
      style={getPositionStyle()}
    >
      {/* Debug info - remove this later */}
      {cursorPosition && (
        <div className="absolute -top-8 left-0 bg-red-500 text-white text-xs p-1 rounded z-50">
          Cursor: {cursorPosition.x}, {cursorPosition.y}
        </div>
      )}
      <div className="flex items-center justify-between p-4 border-b border-border-subdued">
        <h3 className="body-md-bold">Object Attributes</h3>
        <Button
          variant="shapeless"
          size="sm"
          square
          leadingIcon={"close-md" as any}
          onClick={onClose}
          aria-label="Close"
        />
      </div>

      <div className="overflow-y-auto max-h-96">
        <div className="divide-y divide-border-subdued">
          {validAttributes.map(([key, value]) => (
            <ListItem
              key={key}
              primaryContent={
                <p className="text-xs text-text-default mt-2 text-wrap px-1 py-0.5 font-mono bg-black/10 text-neutral-500 inline rounded-sm">
                  {key}
                </p>
              }
              secondaryContent={
                <p className="body-sm text-text-default text-wrap">
                  {String(value)}
                </p>
              }
              action={
                <Icon
                  name={"arrowhead-right-md" as any}
                  className="text-text-subdued"
                />
              }
              onClick={() => onAttributeClick(key, value)}
              classNames={{
                base: "p-3 rounded-none",
                secondaryText: "-my-1 pb-1 pt-2",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AttributeDisplay;
